import { ButtonType, ButtonWidget, DialogPositioningBehavior, DialogType, DialogWidget } from '@tableau/widgets';
import React from 'react';
import { style } from 'typestyle';

import { Messages } from '~src/codegen/Localize';
import { IAnalyticsConnectionMetadata } from '~src/settings/types/ISiteSettingsForSiteAdmin';

import { FlowAnalyticsExtensionConnection } from '../serverApi/types/FlowAnalyticsExtensionConnection';
import { AnalyticsExtensionConnectionPicker } from './AnalyticsExtensionConnectionPicker';
import { useFlowAnalyticsExtensionEditor } from './useFlowAnalyticsExtensionEditor';

const footerClass = style({ display: 'flex', justifyContent: 'flex-end', gap: 12 });
const errorClass = style({ marginTop: 12 });

export interface FlowAnalyticsExtensionDialogProps {
  readonly connection: FlowAnalyticsExtensionConnection;
  readonly loadConnections: () => Promise<ReadonlyArray<IAnalyticsConnectionMetadata>>;
  readonly applyConnection: (connectionLuid: string) => Promise<void>;
  readonly onClose: () => void;
}

export const FlowAnalyticsExtensionDialog: React.FC<FlowAnalyticsExtensionDialogProps> = (props) => {
  const { loadConnections, connection } = props;
  const load = React.useCallback(async () => ({
    options: await loadConnections(),
    connection,
  }), [loadConnections, connection]);
  const editor = useFlowAnalyticsExtensionEditor({
    initialConnection: props.connection,
    load,
    applyConnection: props.applyConnection,
    onApplied: props.onClose,
  });
  const close = () => { if (!editor.isSaving()) { props.onClose(); } };

  return (
    <DialogWidget
      testId="flow-analytics-extension-dialog"
      title={Messages.flowAnalyticsExtensionServerConfiguration()}
      type={DialogType.NormalWithTitleBar}
      positioningBehavior={DialogPositioningBehavior.Centered}
      isModal={true}
      showCloseButton={true}
      onCloseRequest={close}
      shouldReturnFocusOnClose={true}
      dialogWidth={620}
      showFooter={true}
      footerContent={
        <div className={footerClass}>
          <ButtonWidget testId="flow-analytics-extension-cancel" buttonType={ButtonType.Outline} disabled={editor.saving} onClick={close}>
            {Messages.cancel()}
          </ButtonWidget>
          <ButtonWidget testId="flow-analytics-extension-apply" buttonType={ButtonType.Primary}
            disabled={editor.saving || !editor.canApply} onClick={editor.apply}>
            {Messages.apply()}
          </ButtonWidget>
        </div>
      }
    >
      <AnalyticsExtensionConnectionPicker {...editor.pickerProps} />
      {editor.saveFailed && <div className={errorClass} role="alert">{Messages.flowAnalyticsExtensionSaveError()}</div>}
    </DialogWidget>
  );
};
